import {
  IWebchatSettings,
  TWebchatExtraParams,
  TWebchatWidgetFailureReason,
  TWebchatWidgetStatus,
} from 'modules/webchat/domain/entities';
import { IWebchatWidgetProvider } from 'modules/webchat/domain/providers';

const LOADER_BASE_URL = 'https://dash.callbell.eu/include/livechat';
const LOADER_SCRIPT_ID = 'callbell-livechat-loader';
const HOST_ID = 'callbell-livechat-host';

/**
 * DOM interno do bundle livechat.callbell.eu/main.js, sem documentação: o
 * widget não expõe API JS para abrir o chat nem eventos, só o clique no
 * launcher dentro do shadow root aberto do host, e a janela aberta aparece
 * como #callbell-livechat-chat-container. O launcher só é desenhado depois
 * que o POST /token devolve a sala. Pode mudar a qualquer deploy da
 * Callbell; aqui é o único lugar que depende disso.
 */
const LAUNCHER_SELECTOR = 'aside';
const CHAT_CONTAINER_ID = 'callbell-livechat-chat-container';

const IDLE_TIMEOUT_MS = 3000;
const IDLE_FALLBACK_DELAY_MS = 1500;
/**
 * Prazo, contado da injeção do loader, para o launcher aparecer: loader,
 * main.js (cerca de 440 KB) e POST /token.
 */
const READY_TIMEOUT_MS = 15000;
const READY_POLL_INTERVAL_MS = 250;
const READY_MAX_ATTEMPTS = READY_TIMEOUT_MS / READY_POLL_INTERVAL_MS;
/** Limite do open(): espera por ocioso mais o prazo do launcher, com folga. */
const OPEN_TIMEOUT_MS = IDLE_TIMEOUT_MS + READY_TIMEOUT_MS + 2000;

interface ICallbellSettings {
  uuid: string;
  script_token: string;
  extra_params: TWebchatExtraParams;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- augmentation da interface global do browser
  interface Window {
    callbellSettings?: ICallbellSettings;
  }
}

export const buildCallbellLoaderUrl = ({
  uuid,
  scriptToken,
}: IWebchatSettings) =>
  `${LOADER_BASE_URL}/${encodeURIComponent(scriptToken)}/${encodeURIComponent(
    uuid,
  )}.js`;

const runWhenBrowserIsIdle = (task: () => void) => {
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => task(), { timeout: IDLE_TIMEOUT_MS });

      return;
    }

    window.setTimeout(task, IDLE_FALLBACK_DELAY_MS);
  };

  if (document.readyState === 'complete') {
    schedule();

    return;
  }

  window.addEventListener('load', schedule, { once: true });
};

export default class CallbellWebchatWidgetProvider
  implements IWebchatWidgetProvider
{
  private status: TWebchatWidgetStatus = 'idle';

  private failureReason: TWebchatWidgetFailureReason | null = null;

  private visible = true;

  /** Próxima checagem do launcher; null quando nenhuma está agendada. */
  private launcherCheckId: number | null = null;

  private openListeners = new Set<() => void>();

  private statusListeners = new Set<(status: TWebchatWidgetStatus) => void>();

  public mount(settings: IWebchatSettings, extraParams: TWebchatExtraParams) {
    if (this.status !== 'idle' || document.getElementById(LOADER_SCRIPT_ID)) {
      return;
    }

    this.setStatus('loading');

    runWhenBrowserIsIdle(() => this.injectLoader(settings, extraParams));
  }

  public getStatus() {
    return this.status;
  }

  public getFailureReason() {
    return this.failureReason;
  }

  public onStatusChange(listener: (status: TWebchatWidgetStatus) => void) {
    this.statusListeners.add(listener);

    return () => {
      this.statusListeners.delete(listener);
    };
  }

  public setVisible(visible: boolean) {
    this.visible = visible;
    this.applyVisibility();
  }

  public reset() {
    this.stopWaitingForLauncher();
    // O bundle lê callbellSettings.extra_params uma única vez, na montagem: trocar a identidade exige recarregar a página.
    window.setTimeout(() => window.location.reload(), 0);
  }

  public open(): Promise<boolean> {
    if (this.status !== 'loading') {
      return Promise.resolve(this.clickLauncher());
    }

    return new Promise(resolve => {
      let timeoutId = 0;

      const unsubscribe = this.onStatusChange(() => {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve(this.clickLauncher());
      });

      timeoutId = window.setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, OPEN_TIMEOUT_MS);
    });
  }

  public onOpen(listener: () => void) {
    this.openListeners.add(listener);

    return () => {
      this.openListeners.delete(listener);
    };
  }

  private injectLoader(
    settings: IWebchatSettings,
    extraParams: TWebchatExtraParams,
  ) {
    window.callbellSettings = {
      uuid: settings.uuid,
      script_token: settings.scriptToken,
      extra_params: extraParams,
    };

    const script = document.createElement('script');

    script.id = LOADER_SCRIPT_ID;
    script.async = true;
    script.src = buildCallbellLoaderUrl(settings);
    script.onload = () => this.handleLoaderReady();
    script.onerror = () => this.fail('LOADER_ERROR');

    document.body.appendChild(script);

    this.waitForLauncher(1);
  }

  private handleLoaderReady() {
    const host = document.getElementById(HOST_ID);

    // O loader cria o host ao executar; sem ele, caiu no ramo de erro (token, uuid ou domínio).
    if (!host) {
      this.fail('HOST_NOT_CREATED');

      return;
    }

    // A rota pode ter mudado enquanto o loader carregava.
    this.applyVisibility();
    // Captura no host: roda antes do clique chegar ao widget e trocar o estado.
    host.addEventListener('click', this.handleHostClick, true);
  }

  private waitForLauncher(attempt: number) {
    this.launcherCheckId = null;

    if (this.status !== 'loading') return;

    if (this.getLauncher()) {
      this.setStatus('ready');

      return;
    }

    if (attempt >= READY_MAX_ATTEMPTS) {
      this.fail(
        document.getElementById(HOST_ID)
          ? 'LAUNCHER_TIMEOUT'
          : 'HOST_NOT_CREATED',
      );

      return;
    }

    this.launcherCheckId = window.setTimeout(
      () => this.waitForLauncher(attempt + 1),
      READY_POLL_INTERVAL_MS,
    );
  }

  private stopWaitingForLauncher() {
    if (this.launcherCheckId === null) return;

    window.clearTimeout(this.launcherCheckId);
    this.launcherCheckId = null;
  }

  private fail(reason: TWebchatWidgetFailureReason) {
    if (this.status !== 'loading') return;

    this.failureReason = reason;
    this.setStatus('failed');
    // Um widget que aparecer depois do prazo fica escondido: o site já mostrou a alternativa.
    this.applyVisibility();
  }

  private setStatus(status: TWebchatWidgetStatus) {
    // Pronto ou falhou, por qualquer caminho: não há mais launcher para esperar.
    if (status !== 'loading') this.stopWaitingForLauncher();

    this.status = status;
    this.statusListeners.forEach(listener => listener(status));
  }

  private clickLauncher() {
    if (!this.visible || this.status !== 'ready') return false;

    if (this.isChatOpen()) return true;

    const launcher = this.getLauncher();

    if (!launcher) return false;

    launcher.click();

    return true;
  }

  private handleHostClick = (event: Event) => {
    const isLauncherClick = event
      .composedPath()
      .some(
        target =>
          target instanceof Element && target.matches(LAUNCHER_SELECTOR),
      );

    if (!isLauncherClick || this.isChatOpen()) return;

    this.openListeners.forEach(listener => listener());
  };

  private getShadowRoot() {
    return document.getElementById(HOST_ID)?.shadowRoot ?? null;
  }

  private getLauncher() {
    return (
      this.getShadowRoot()?.querySelector<HTMLElement>(LAUNCHER_SELECTOR) ??
      null
    );
  }

  private isChatOpen() {
    return Boolean(this.getShadowRoot()?.getElementById(CHAT_CONTAINER_ID));
  }

  private applyVisibility() {
    const host = document.getElementById(HOST_ID);

    if (host) {
      host.style.display =
        this.visible && this.status !== 'failed' ? '' : 'none';
    }
  }
}
